import * as React from 'react'
import Box from '@mui/material/Box'
import { WWMXGrid } from '@user/components/xGrid'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import TextField from '@mui/material/TextField'
import { useActions } from './useActions'
import { useGetColumnData } from '@user/components/Leads/AddLeadPage/FormComponents/DuplicateAlert/DuplicateDialog/WorkwaveContacts/useGetColumnData'
import {
  workWaveContactDuplicatesDefaultColumns,
  workWaveContactDuplicatesDefaultColVis,
} from '@user/components/Leads/AddLeadPage/FormComponents/DuplicateAlert/DuplicateDialog/WorkwaveContacts/tableColumn'
import { useData } from './useData'
import useMediaQuery from '@mui/material/useMediaQuery'

export interface WorkWaveContactsDuplicatesProps {
  highlights?: Record<string, string>
  handleDuplicateManageClick?: () => void
  onDialogClose: () => void
  loading?: boolean
}

export const WorkWaveContactsDuplicates = ({
  highlights,
  handleDuplicateManageClick,
  onDialogClose,
  loading,
}: WorkWaveContactsDuplicatesProps) => {
  const {
    duplicatesCount,
    orderBy,
    page,
    perPage,
    densityMode,
    onStateChange,
    handleSearchChange,
    tableRows,
    onClickManage,
    searchText,
  } = useData({ handleDuplicateManageClick, onDialogClose })

  const {
    loading: colsLoading,
    columns,
    columnVisibility,
    apiRef,
  } = useGetColumnData({
    onClickManage: onClickManage,
    densityMode: densityMode,
    highlights,
  })

  const {
    onColumnWidthChange,
    onSaveColumnState,
    onSortModelChange,
    sortModel,
    handlePaginationModelChange,
  } = useActions({ columns, orderBy })

  const isMobile = useMediaQuery('(max-width:900px)')

  return (
    <Box minHeight={'calc(100svh - 226px)'} bgcolor={'#F9FAFB'}>
      <TextField
        sx={{ position: 'absolute', right: '20px', zIndex: 1000 }}
        onChange={handleSearchChange}
        value={searchText}
        placeholder="Search..."
        spellCheck={false}
        variant="standard"
        type="search"
        InputProps={{
          sx: {
            padding: (theme) => `${theme.spacing(0.9)} ${theme.spacing(1.2)}`,
            '&::after': {
              borderBottom: '1px solid #',
            },
            '&::before': {
              borderBottom: '1px solid #ffffff00',
            },
            borderBottom: 'none',
          },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton size="small">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <WWMXGrid
        applyCustomStyles={true}
        apiRef={apiRef}
        virtualScrollerHeight={
          densityMode === 'compact'
            ? 316
            : densityMode === 'standard'
              ? 333
              : 350
        }
        // columns
        columnVisibilityModel={columnVisibility}
        columns={colsLoading ? [] : columns}
        onColumnWidthChange={(e) =>
          onColumnWidthChange(e?.colDef, columns, columnVisibility)
        }
        columnsSelectorProps={{
          saveCallback: (e) => onSaveColumnState(e, columns),
          defaultColumns: workWaveContactDuplicatesDefaultColumns,
          defaultVisibility: workWaveContactDuplicatesDefaultColVis,
          lockedColumns: ['actions'],
        }}
        pinnedColumns={{ left: !isMobile ? ['actions'] : [] }}
        //state
        isDensityEnable
        density={densityMode}
        onStateChange={onStateChange}
        // rows
        loading={loading || colsLoading}
        rows={colsLoading ? [] : tableRows}
        rowCount={duplicatesCount}
        // pagination
        paginationModel={{ page: page - 1, pageSize: perPage }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50]}
        // sorting
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        width="100%"
        hideDefaultBtn={true}
      />
    </Box>
  )
}

WorkWaveContactsDuplicates.displayName = 'WorkWaveContactsDuplicates'
